import {
  ApartmentOutlined,
  HomeOutlined,
  LogoutOutlined,
  SwapOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useIntl } from '@umijs/max';
import {
  AvatarDropdown as CoreAvatarDropdown,
  useSession,
  useShell,
  useTenant,
} from '@zhiyue/core';
import { Avatar, type MenuProps } from 'antd';
import { BizAppConfig } from 'config/application.manifest';
import React from 'react';

const AvatarDropdown: React.FC = () => {
  const intl = useIntl();
  const { currentUser } = useSession();
  const { tenants, currentTenantUuid } = useTenant();
  const { shellConfig } = useShell();
  const userMenuLabels = shellConfig.ui.userMenu.labels;
  const resolvedCurrentUser = currentUser as
    | (typeof currentUser & API.CurrentUser)
    | null;

  const personalCenterUrl = BizAppConfig.appPersonalCenterUrl;
  const workbenchUrl = BizAppConfig.appWorkbenchUrl?.trim();
  const tenantManagementBaseUrl = BizAppConfig.appTenantManagementUrl?.trim();
  const activeTenantUuid = currentTenantUuid || currentUser?.tenantUuid;
  const activeTenant = React.useMemo(
    () => tenants.find((tenant) => tenant.id === activeTenantUuid),
    [activeTenantUuid, tenants],
  );

  const tenantName = React.useMemo(() => {
    if (!activeTenantUuid) {
      return userMenuLabels.unknownTenant;
    }

    return activeTenant?.name || userMenuLabels.unknownTenant;
  }, [activeTenant, activeTenantUuid, userMenuLabels.unknownTenant]);

  const isCurrentTenantAdmin = activeTenant?.roleLabel === '管理员';

  const tenantManagementUrl = React.useMemo(() => {
    if (!tenantManagementBaseUrl || !activeTenantUuid) {
      return '';
    }

    const organizationUuid =
      resolvedCurrentUser?.organizationUuid ||
      activeTenant?.organizations?.[0]?.orgId ||
      activeTenant?.organizations?.[0]?.id ||
      '';

    if (
      tenantManagementBaseUrl.includes('{tenantUuid}') ||
      tenantManagementBaseUrl.includes('{organizationUuid}')
    ) {
      return tenantManagementBaseUrl
        .replaceAll('{tenantUuid}', encodeURIComponent(activeTenantUuid))
        .replaceAll('{organizationUuid}', encodeURIComponent(organizationUuid));
    }

    if (typeof window === 'undefined') {
      return tenantManagementBaseUrl;
    }

    const url = new URL(tenantManagementBaseUrl, window.location.origin);
    url.searchParams.set('tenantUuid', activeTenantUuid);
    if (organizationUuid) {
      url.searchParams.set('organizationUuid', organizationUuid);
    }

    return url.toString();
  }, [
    activeTenant?.organizations,
    activeTenantUuid,
    resolvedCurrentUser?.organizationUuid,
    tenantManagementBaseUrl,
  ]);

  const currentMemberLabel = React.useMemo(() => {
    return currentUser?.nickname || currentUser?.username || '-';
  }, [currentUser?.nickname, currentUser?.username]);

  const avatarFallback = React.useMemo(() => {
    const normalizedLabel = currentMemberLabel?.trim();
    const chars = Array.from(normalizedLabel || '');

    return (chars.slice(-2).join('') || 'U').toUpperCase();
  }, [currentMemberLabel]);

  const avatarSrc = React.useMemo(() => {
    const value = currentUser?.avatar?.trim();
    if (!value) {
      return undefined;
    }
    try {
      const url = new URL(
        value,
        typeof window === 'undefined'
          ? 'http://localhost'
          : window.location.origin,
      );
      if (url.hostname === 'api.multiavatar.com') {
        return undefined;
      }
    } catch {
      return value;
    }
    return value;
  }, [currentUser?.avatar]);

  const items = React.useMemo<Required<MenuProps>['items']>(() => {
    const menuItems: Required<MenuProps>['items'] = [
      {
        key: 'center',
        icon: <UserOutlined />,
        label: intl.formatMessage({ id: 'menu.account.center' }),
        disabled: !personalCenterUrl,
      },
      {
        key: 'workbench',
        icon: <HomeOutlined />,
        label: intl.formatMessage({ id: 'menu.account.workbench' }),
        disabled: !workbenchUrl,
      },
    ];

    if (shellConfig.featureFlags.enableTenantSwitch) {
      menuItems.push(
        { type: 'divider' },
        {
          key: 'currentOrg',
          label: `${userMenuLabels.currentTenant}: ${tenantName}`,
          disabled: true,
        },
        {
          key: 'switchOrg',
          icon: <SwapOutlined />,
          label: userMenuLabels.switchTenant,
        },
      );
    }

    if (isCurrentTenantAdmin) {
      menuItems.push(
        { type: 'divider' },
        {
          key: 'tenantManagement',
          icon: <ApartmentOutlined />,
          label: intl.formatMessage({ id: 'menu.account.tenantManagement' }),
          disabled: !tenantManagementUrl,
        },
      );
    }

    menuItems.push(
      { type: 'divider' },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: intl.formatMessage({ id: 'menu.account.logout' }),
      },
    );

    return menuItems;
  }, [
    intl,
    personalCenterUrl,
    workbenchUrl,
    shellConfig.featureFlags.enableTenantSwitch,
    isCurrentTenantAdmin,
    tenantName,
    tenantManagementUrl,
    userMenuLabels.currentTenant,
    userMenuLabels.switchTenant,
  ]);

  const handleAction = React.useCallback(
    (key: string) => {
      if (key === 'center' && personalCenterUrl) {
        window.open(personalCenterUrl, '_self');
      }

      if (key === 'workbench' && workbenchUrl) {
        window.open(workbenchUrl, '_self');
      }

      if (key === 'tenantManagement' && tenantManagementUrl) {
        window.open(tenantManagementUrl, '_self');
      }
    },
    [personalCenterUrl, workbenchUrl, tenantManagementUrl],
  );

  return (
    <CoreAvatarDropdown items={items} onAction={handleAction}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          cursor: 'pointer',
        }}
      >
        <Avatar size={22} src={avatarSrc}>
          {avatarFallback}
        </Avatar>
        <span>{currentMemberLabel}</span>
      </div>
    </CoreAvatarDropdown>
  );
};

export default AvatarDropdown;
